// WHAT: Dashboard-Organism zur Darstellung eines Korrekturfaktors (Real TDEE vs. Oura TDEE) inkl. Gewichtstrend und Confidence
// WHY:  Verknüpft Oura-Daten und Intake-Informationen zu einer einfach interpretierbaren Metrik für Energie-Balance
// DOMAIN: core → dashboard → organisms
// DEPENDENCIES: `useCorrectionFactor`-Hook (Analysergebnis), `useSmartSync` für globale Daten-Synchronisation, lucide-react Icons
// RISKS:
//   - NOTE: Vertraut auf das Backend-Modell von `useCorrectionFactor`; Änderungen an Feldern wie `confidence` oder `real_tdee` müssen synchron erfolgen
//   - NOTE: `handleSync` führt Full-Sync aus und refresht anschließend nur lokal; Race-Conditions mit anderen Sync-Mechanismen sind möglich
import { TrendingUp, TrendingDown, AlertCircle, RefreshCw } from "lucide-react";

import { useCorrectionFactor } from "../../hooks/useCorrectionFactor";
import { useSmartSync } from "../../hooks/useSmartSync";

interface CorrectionFactorProperties {
  showCard?: boolean;
}

/**
 * WHAT:
 *   Zeigt den berechneten Oura-Korrekturfaktor, den daraus abgeleiteten „Real TDEE“ sowie
 *   Sekundärmetriken (Intake, Oura TDEE, Gewichtsdifferenz) und einen Confidence-Hinweis an.
 *
 * WHY:
 *   Dient als zentrales Diagnoseinstrument für die Kalorien- und Aktivitätskalibrierung, basierend
 *   auf historischen Oura- und Intake-Daten.
 *
 * PROPS:
 *   - showCard?: boolean — Reserviert für zukünftig alternative Layouts (z. B. Card vs. Inline).
 *
 * BEHAVIOR:
 *   - Lädt Daten via `useCorrectionFactor` (inkl. `loading`/`error`-States).
 *   - Triggert bei „Sync“-Klick `useSmartSync().syncAll()` und anschließend `refetch()`, um den Faktor zu aktualisieren.
 *   - Interpretiert `confidence` heuristisch als Ampel (🔴/🟡/🟢) und färbt den Text entsprechend.
 *
 * RISKS:
 *   - NOTE: `getConfidenceColor` arbeitet string-basiert; Änderungen an den Emoji-Codes brechen die Farblogik.
 *   - NOTE: Gewichtsdeltas werden rein numerisch angezeigt; keine weitere Kontextualisierung (z. B. Zeitraum) erfolgt hier.
 *
 * @param root0 - Component props
 *
 * @param root0.showCard - Reserved for future alternative layouts
 *
 * @returns {JSX.Element} Correction factor component
 *
 */
export function CorrectionFactor({ showCard = false }: CorrectionFactorProperties = {}) {
  const { data, error, loading, refetch } = useCorrectionFactor();
  const { isLoading: isSyncing, syncAll } = useSmartSync();

  // NOTE: Reserved for future alternative layouts (Card vs. Inline)
  // Reason: Parameter reserved for future use, currently unused but part of API
  if (showCard) {
    // Reserved for future implementation
  }

  const handleSync = () => {
    (async () => {
      try {
        await syncAll();
        // Refetch correction factor data after sync
        await refetch();
      } catch {
        // NOTE: Sync failures are surfaced via hook state; additional logging can be added via debug system
      }
    })().catch(() => {
      // Sync failures are handled via hook state
    });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-6 w-48 bg-slate-700 animate-pulse rounded" />
        <div className="grid grid-cols-2 gap-4">
          <div className="h-20 bg-slate-700 animate-pulse rounded" />
          <div className="h-20 bg-slate-700 animate-pulse rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-red-400">
        <AlertCircle className="w-5 h-5" />
        <p>Fehler beim Laden der Daten: {error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center gap-2 text-slate-400">
        <AlertCircle className="w-5 h-5" />
        <p>Keine Daten verfügbar</p>
      </div>
    );
  }

  const getConfidenceColor = () => {
    if (data.confidence.includes("🔴")) {
      return "text-red-500";
    }
    if (data.confidence.includes("🟡")) {
      return "text-yellow-500";
    }
    if (data.confidence.includes("🟢")) {
      return "text-green-500";
    }
    return "text-emerald-500";
  };

  const isGaining = data.delta_kg > 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-400">
            {new Date(data.date).toLocaleDateString("de-DE")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className={`text-sm font-medium ${getConfidenceColor()}`}>{data.confidence}</div>
          <button
            className="p-1 rounded-md hover:bg-slate-700 transition-colors disabled:opacity-50"
            disabled={isSyncing}
            onClick={handleSync}
            title="Daten synchronisieren"
          >
            <RefreshCw className={`w-4 h-4 text-slate-400 ${isSyncing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Main Metrics */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <p className="text-sm text-slate-400">Oura Faktor</p>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-white">{data.oura_factor.toFixed(3)}</span>
            <span className="text-sm text-slate-400">×</span>
          </div>
        </div>

        <div className="space-y-1">
          <p className="text-sm text-slate-400">Real TDEE</p>
          <p className="text-2xl font-semibold text-white">
            {Math.round(data.real_tdee).toLocaleString("de-DE")}
            <span className="text-sm font-normal text-slate-400 ml-1">kcal</span>
          </p>
        </div>
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-3 gap-3 pt-3 border-t border-slate-700">
        <div>
          <p className="text-xs text-slate-400">Intake</p>
          <p className="text-sm font-medium text-white">{Math.round(data.intake)} kcal</p>
        </div>
        <div>
          <p className="text-xs text-slate-400">Oura TDEE</p>
          <p className="text-sm font-medium text-white">{Math.round(data.oura_tdee)} kcal</p>
        </div>
        <div>
          <p className="text-xs text-slate-400">Gewicht Δ</p>
          <div className="flex items-center gap-1">
            {isGaining ? (
              <TrendingUp className="w-3 h-3 text-red-500" />
            ) : (
              <TrendingDown className="w-3 h-3 text-green-500" />
            )}
            <p className={`text-sm font-medium ${isGaining ? "text-red-500" : "text-green-500"}`}>
              {data.delta_kg > 0 ? "+" : ""}
              {data.delta_kg.toFixed(2)} kg
            </p>
          </div>
        </div>
      </div>

      {/* Warning */}
      {data.warning ? (
        <div className="flex items-start gap-2 p-3 bg-slate-700/50 rounded-lg">
          <AlertCircle className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-slate-400 leading-relaxed">{data.warning}</p>
        </div>
      ) : null}
    </div>
  );
}
